/**
 * Splits a DRF error response into per-field messages and a general
 * (non-field) message, so a form can map the former onto each MUI
 * `TextField`'s `error`/`helperText` and the latter onto a single top-level
 * `Alert`.
 *
 * DRF shape: `{ field: [messages], ..., non_field_errors: [messages] }`.
 * Any key that isn't in `knownFields` (no input to attach it to — e.g. a
 * hidden/derived field, or one the form doesn't render) is folded into the
 * general message too, same as `non_field_errors`.
 */
export function parseApiFormErrors(error, knownFields = []) {
  const data = error?.response?.data;
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return { fieldErrors: {}, formError: null };
  }

  const fieldErrors = {};
  const formMessages = [];

  Object.entries(data).forEach(([key, value]) => {
    const messages = Array.isArray(value) ? value : [value];
    if (key !== 'non_field_errors' && knownFields.includes(key)) {
      fieldErrors[key] = messages.join(' ');
    } else {
      formMessages.push(...messages);
    }
  });

  return {
    fieldErrors,
    formError: formMessages.length > 0 ? formMessages.join(' ') : null,
  };
}

/**
 * Same split for a FastAPI 422 (`{ detail: [{ loc, msg, type }] }`), as the
 * crm_service answers. `loc` ['body', 'credentials', 'user'] becomes the
 * field 'credentials.user'; `describe(item)` turns each item into the text
 * shown (e.g. a translated "required" for `type: 'missing'`).
 */
export function parseFastApiFormErrors(error, knownFields = [], describe = (item) => item.msg) {
  const detail = error?.response?.data?.detail;
  if (!Array.isArray(detail)) return { fieldErrors: {}, formError: null };

  const fieldErrors = {};
  const formMessages = [];
  detail.forEach((item) => {
    const field = (item.loc ?? []).filter((part) => part !== 'body').join('.');
    if (knownFields.includes(field)) {
      fieldErrors[field] = describe(item);
    } else {
      formMessages.push(describe(item));
    }
  });

  return {
    fieldErrors,
    formError: formMessages.length > 0 ? formMessages.join(' ') : null,
  };
}
